var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import SongMetadata from "./SongMetadata.js";
import { SimpleEvent } from "../../HelperModules/SimpleEvent.js";
import { OscillatorTrack } from "../Tracks/OscillatorTrack.js";
import { SoundFileTrack } from "../Tracks/SoundFileTrack.js";
import { ConnectionManager } from "./ConnectionManager.js";
import { setContext } from "../../../dependencies/tuna.js";
export class SongManager {
    constructor(context) {
        this.lookaheadTime = 0.125;
        this._playing = false;
        this._startTime = 0;
        this._quarterNotePosition = 0;
        this.playingIntervalIDs = null;
        this.metadata = new SongMetadata();
        this.context = (context === undefined) ? new AudioContext() : context;
        this.connectionManager = new ConnectionManager(this.context);
        this.scheduleEvent = new SimpleEvent();
        this.playingChangedEvent = new SimpleEvent();
        this.quarterNotePositionChangedEvent = new SimpleEvent();
        this._tracks = [];
        this._boundQuarterNoteUpdateFunction = this.quarterNoteUpdateFunction.bind(this);
    }
    get playing() {
        return this._playing;
    }
    get quarterNotePosition() {
        return this._quarterNotePosition;
    }
    set quarterNotePosition(value) {
        this._internalQuarterNotePosition = value;
        this._startTime = this.context.currentTime - this.metadata.positionQuarterNoteToSeconds(this._quarterNotePosition);
        this._scheduleStop();
        if (this._playing) {
            this._tracks.forEach(element => {
                element.start(this._quarterNotePosition);
            });
        }
    }
    set _internalQuarterNotePosition(value) {
        this._quarterNotePosition = value;
        this.quarterNotePositionChangedEvent.emit(value);
    }
    get tracks() {
        return this._tracks;
    }
    addOscillatorTrack(settings) {
        let newTrack = new OscillatorTrack(this.metadata, this.context, this.scheduleEvent, this.connectionManager, settings);
        this._tracks.push(newTrack);
        return newTrack;
    }
    addSoundFileTrack(settings) {
        return __awaiter(this, void 0, void 0, function* () {
            let newTrack = yield SoundFileTrack.create(this.metadata, this.context, this.scheduleEvent, this.connectionManager, settings);
            this._tracks.push(newTrack);
            return newTrack;
        });
    }
    removeTrack(track) {
        let index = this._tracks.indexOf(track);
        if (index != -1) {
            this._tracks.splice(index, 1);
        }
        track.destroy();
    }
    start(startPosition = this.quarterNotePosition) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.context.state === "suspended") {
                yield this.context.resume();
            }
            this._playing = true;
            this.playingChangedEvent.emit(this._playing);
            this.quarterNotePosition = startPosition;
            if (startPosition == 0) {
                this._startTime = this.context.currentTime;
            }
            else {
                this._startTime = this.context.currentTime - this.metadata.positionQuarterNoteToSeconds(startPosition);
            }
            this.connectionManager.outputGain.gain.setTargetAtTime(0.9999, this.context.currentTime, 0.03);
            this.playingIntervalIDs = setInterval(() => { this.scheduleNotes(); }, 50);
            requestAnimationFrame(this._boundQuarterNoteUpdateFunction);
            this._tracks.forEach(element => {
                element.start(this._quarterNotePosition);
            });
            this._scheduleStop();
        });
    }
    stop() {
        this._playing = false;
        this.playingChangedEvent.emit(this._playing);
        this.connectionManager.outputGain.gain.setTargetAtTime(0.0001, this.context.currentTime, 0.03);
        this._startSongLength = null;
        clearInterval(this.playingIntervalIDs);
        this._tracks.forEach(element => {
            element.stop();
        });
    }
    stopToBeginning() {
        this.stop();
        this.quarterNotePosition = 0;
    }
    serialise() {
        let serialisedTracks = [];
        this._tracks.forEach(track => {
            let trackSettings = track.serialise();
            serialisedTracks.push(trackSettings);
        });
        let serialisedChains = this.connectionManager.serialiseChains();
        return {
            "fileType": "SequencerSongSettings",
            "version": SongManager.saveFileVersion,
            "metadataEvents": this.metadata.serialise(),
            "tracks": serialisedTracks,
            "chains": serialisedChains
        };
    }
    deserialise(settings) {
        return __awaiter(this, void 0, void 0, function* () {
            if (settings.version != SongManager.saveFileVersion) {
                console.log("WARNING: Save file is an old version, loading may not function correctly.");
            }
            this._tracks.forEach(track => { track.destroy(); });
            this._tracks = [];
            this.connectionManager.deserialiseChains(settings.chains);
            this.metadata.deserialise(settings.metadataEvents);
            for (let i = 0; i < settings.tracks.length; i++) {
                let track = settings.tracks[i];
                switch (track.source.type) {
                    case "oscillator":
                        this.addOscillatorTrack(track);
                        break;
                    case "soundFile":
                        yield this.addSoundFileTrack(track);
                        break;
                    default:
                        throw new Error("Invalid track type.");
                        break;
                }
            }
            ;
        });
    }
    destroy() {
        this.scheduleEvent.removeAllListeners();
        this.playingChangedEvent.removeAllListeners();
    }
    saveToWAV() {
        return __awaiter(this, void 0, void 0, function* () {
            let length = this.getPlaybackLength();
            if (length > 0) {
                let song = this.serialise();
                let offlineManager = new OfflineSongManager(length + 0.1);
                yield offlineManager.deserialise(song);
                let result = yield offlineManager.saveToWAV();
                setContext(this.context);
                return result;
            }
            else {
                throw new Error("Song Length must be greater than 0 to save to WAV.");
            }
        });
    }
    getPlaybackLength() {
        let longestEvent = 0;
        let cachedEffectLengths = {};
        this._tracks.forEach(track => {
            let playbackTime = this.metadata.positionQuarterNoteToSeconds(track.timeline.playbackTime);
            let connection = this.connectionManager.getConnections(track.audioSource)[0];
            if (connection != "Context") {
                if (connection in cachedEffectLengths) {
                    playbackTime += cachedEffectLengths[connection];
                }
                else {
                    let chainDelayTime = this.connectionManager.getChainDelayTime(connection);
                    playbackTime += chainDelayTime;
                    cachedEffectLengths[connection] = chainDelayTime;
                }
            }
            if (playbackTime > longestEvent) {
                longestEvent = playbackTime;
            }
        });
        return (longestEvent > 0 ? longestEvent + 0.01 : 0);
    }
    scheduleNotes() {
        let quarterNoteTime = this.quarterNotePosition + ((this.lookaheadTime / this.metadata.getSecondsPerBeat(this.quarterNotePosition))
            / this.metadata.getQuarterNoteMultiplier(this.quarterNotePosition));
        this.scheduleEvent.emit(quarterNoteTime);
    }
    quarterNoteUpdateFunction() {
        if (this.playing) {
            let timeSinceStart = this.context.currentTime - this._startTime;
            this._internalQuarterNotePosition = this.metadata.positionSecondsToQuarterNote(timeSinceStart);
            if (this._startSongLength && timeSinceStart > this._startSongLength) {
                this.stopToBeginning();
            }
            requestAnimationFrame(this._boundQuarterNoteUpdateFunction);
        }
    }
    _scheduleStop() {
        let length = this.getPlaybackLength();
        if (length > 0) {
            this._startSongLength = length;
        }
        else {
            this._startSongLength = null;
        }
    }
}
SongManager.saveFileVersion = "1.0.0";
export class OfflineSongManager extends SongManager {
    constructor(length) {
        super(new OfflineAudioContext(2, length * 44100, 44100));
    }
    saveToWAV() {
        return __awaiter(this, void 0, void 0, function* () {
            this._tracks.forEach(track => {
                track.scheduleAllEvents();
            });
            console.log("Starting AudioBuffer render");
            let audioBuffer = yield this.context.startRendering();
            console.log("Finished AudioBuffer render");
            const [left, right] = [audioBuffer.getChannelData(0), audioBuffer.getChannelData(1)];
            const interleaved = new Float32Array(left.length * 2);
            for (let src = 0, dst = 0; src < left.length; src++, dst += 2) {
                interleaved[dst] = left[src];
                interleaved[dst + 1] = right[src];
            }
            ;
            const wavBytes = getWavBytes(interleaved.buffer, {
                isFloat: true,
                numChannels: 2,
                sampleRate: 44100,
            });
            return new Blob([wavBytes], { type: "audio/wav" });
        });
    }
}
function getWavBytes(buffer, options) {
    const type = options.isFloat ? Float32Array : Uint16Array;
    const numFrames = buffer.byteLength / type.BYTES_PER_ELEMENT;
    const headerBytes = getWavHeader(Object.assign({}, options, { numFrames }));
    const wavBytes = new Uint8Array(headerBytes.length + buffer.byteLength);
    wavBytes.set(headerBytes, 0);
    wavBytes.set(new Uint8Array(buffer), headerBytes.length);
    return wavBytes;
}
function getWavHeader(options) {
    const numFrames = options.numFrames;
    const numChannels = options.numChannels || 2;
    const sampleRate = options.sampleRate || 44100;
    const bytesPerSample = options.isFloat ? 4 : 2;
    const format = options.isFloat ? 3 : 1;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = numFrames * blockAlign;
    const buffer = new ArrayBuffer(44);
    const dv = new DataView(buffer);
    let p = 0;
    function writeString(s) {
        for (let i = 0; i < s.length; i++) {
            dv.setUint8(p + i, s.charCodeAt(i));
        }
        p += s.length;
    }
    function writeUint32(d) {
        dv.setUint32(p, d, true);
        p += 4;
    }
    function writeUint16(d) {
        dv.setUint16(p, d, true);
        p += 2;
    }
    writeString('RIFF');
    writeUint32(dataSize + 36);
    writeString('WAVE');
    writeString('fmt ');
    writeUint32(16);
    writeUint16(format);
    writeUint16(numChannels);
    writeUint32(sampleRate);
    writeUint32(byteRate);
    writeUint16(blockAlign);
    writeUint16(bytesPerSample * 8);
    writeString('data');
    writeUint32(dataSize);
    return new Uint8Array(buffer);
}
//# sourceMappingURL=SongManager.js.map